import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { LifeBuoy } from 'lucide-react';

import { Spinner } from '@/components/ui/spinner';

import { useSupportSessionsApi } from '../../../common/hooks/use-support-sessions-api.hook';
import AuthClientStore from '../../../common/services/auth-client.service';
import { supportFrameSessionId } from '../../../common/services/support-frame';
import { EmptyState } from '../../../components/design-system/admin/EmptyState';

/**
 * `/support/:sessionId`, the first page of a support session frame. Signed in
 * as the super admin, it asks for the session's token, keeps it for this frame
 * only, and reloads the frame into the vendor's portal. Anywhere else (a tab of
 * its own) it opens nothing.
 */
export function SupportSessionStartPage() {
    const { t } = useTranslation();
    const { sessionId } = useParams();
    const api = useSupportSessionsApi();
    const [failed, setFailed] = useState(false);
    const once = useRef(false);

    useEffect(() => {
        // StrictMode runs effects twice: one token is enough.
        if (once.current) return;
        once.current = true;
        if (supportFrameSessionId() !== sessionId) {
            setFailed(true);
            return;
        }
        (async () => {
            try {
                const { access_token } = await api.tokenFor(sessionId);
                AuthClientStore.setSupportToken(access_token);
                window.location.replace('/dashboard');
            } catch (error) {
                console.error('Error opening support session:', error);
                setFailed(true);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId]);

    if (failed) {
        return (
            <div className="flex min-h-[60vh] items-center justify-center" data-testid="support-session-start-failed">
                <EmptyState
                    icon={<LifeBuoy />}
                    title={t('SUPPORT_ACCESS.START_ERROR_TITLE')}
                    description={t('SUPPORT_ACCESS.START_ERROR_BODY')}
                />
            </div>
        );
    }

    return (
        <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 text-sm text-muted-foreground" data-testid="support-session-starting">
            <Spinner />
            {t('SUPPORT_ACCESS.STARTING')}
        </div>
    );
}

export default SupportSessionStartPage;
